/** CIS ledger → CIS contractors: every supplier the Cost Inbox treats as a CIS sub-contractor, with their deduction status (20 / 30 / gross), UTR and how they got there (set by admin · learned). */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Check, X, Sparkles, UserCog, Trash2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CIS_RATES, VAT_TREATMENTS, rateLabel } from '../../components/cost-inbox/cisRules';

const gbp = (v) => `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const uk = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');
const field = 'bg-[#1E2530] border-white/20 text-white h-8 text-sm';

function RatePills({ value, onChange, disabled, testid }) {
  return (
    <div className="inline-flex rounded-md overflow-hidden border border-white/15" data-testid={testid}>
      {CIS_RATES.map(o => (
        <button key={o.value} type="button" disabled={disabled} onClick={() => onChange(o.value)} title={o.hint}
          className={`px-2.5 h-7 text-xs font-medium transition-colors ${Number(value) === o.value ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-[#1E2530] text-gray-300 hover:bg-white/5'}`}
          data-testid={`${testid}-${o.value}`}>{o.label}</button>
      ))}
    </div>
  );
}

function AddContractor({ api, onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [rate, setRate] = useState(20);
  const [vt, setVt] = useState('no_vat');
  const [utr, setUtr] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (name.trim().length < 2) { toast.error("Enter the sub-contractor's name"); return; }
    setBusy(true);
    try {
      const r = await api.post('/cost-inbox/cis-suppliers', { supplier_name: name.trim(), rate, vat_treatment: vt, utr: utr.trim() || null, cis: true });
      toast.success(`${r.data.supplier.supplier_name} added · ${rateLabel(rate)}`);
      setOpen(false); setName(''); setRate(20); setVt('no_vat'); setUtr('');
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };
  if (!open) {
    return <Button onClick={() => setOpen(true)} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="cis-add-contractor-btn"><Plus className="w-4 h-4 mr-1.5" /> Add CIS contractor</Button>;
  }
  return (
    <div className="rounded-lg bg-[#2A3240] border border-[#F5A623]/40 p-3 flex flex-wrap items-end gap-3 w-full" data-testid="cis-add-contractor-form">
      <div className="flex-1 min-w-[200px]">
        <label className="text-[11px] text-gray-400">Sub-contractor · as it appears on their invoices</label>
        <Input value={name} onChange={e => setName(e.target.value)} className={field} placeholder="e.g. J Smith Carpentry" autoFocus data-testid="cis-add-name" />
      </div>
      <div>
        <label className="text-[11px] text-gray-400 block mb-1">CIS status</label>
        <RatePills value={rate} onChange={setRate} testid="cis-add-rate" />
      </div>
      <div>
        <label className="text-[11px] text-gray-400 block mb-1">VAT</label>
        <div className="inline-flex rounded-md overflow-hidden border border-white/15" data-testid="cis-add-vat">
          {VAT_TREATMENTS.map(o => <button key={o.value} type="button" onClick={() => setVt(o.value)} className={`px-2.5 h-7 text-xs ${vt === o.value ? 'bg-[#F5A623] text-[#1E2530] font-medium' : 'bg-[#1E2530] text-gray-300 hover:bg-white/5'}`} data-testid={`cis-add-vat-${o.value}`}>{o.value === 'no_vat' ? 'No VAT' : 'Reverse charge'}</button>)}
        </div>
      </div>
      <div className="w-36">
        <label className="text-[11px] text-gray-400">UTR (optional)</label>
        <Input value={utr} onChange={e => setUtr(e.target.value)} className={field} placeholder="10 digits" data-testid="cis-add-utr" />
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => setOpen(false)} className="text-gray-300 h-8" data-testid="cis-add-cancel">Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold h-8" data-testid="cis-add-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save</>}</Button>
      </div>
    </div>
  );
}

function ContractorRow({ s, api, onChanged }) {
  const [utr, setUtr] = useState(s.utr || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setUtr(s.utr || ''); }, [s.utr]);
  const patch = async (body, msg) => {
    setBusy(true);
    try { await api.patch(`/cost-inbox/cis-suppliers/${s.id}`, body); toast.success(msg); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Forget ${s.supplier_name}'s CIS record? Their past invoices stay as posted; the reader can learn them again.`)) return;
    setBusy(true);
    try { await api.delete(`/cost-inbox/cis-suppliers/${s.id}`); toast.success('Removed'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
    finally { setBusy(false); }
  };
  const pending = !s.cis && s.source === 'pending';
  return (
    <tr className={`border-t border-white/5 [&>td]:px-3 [&>td]:py-2 ${!s.cis ? 'opacity-70' : ''}`} data-testid={`cis-contractor-${s.id}`}>
      <td>
        <div className="text-white font-semibold" data-testid={`cis-contractor-name-${s.id}`}>{s.supplier_name}</div>
        <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
          {s.source === 'learned' ? <><Sparkles className="w-3 h-3 text-emerald-300" /> Learned from {s.invoices_seen} invoice{s.invoices_seen === 1 ? '' : 's'}</>
            : s.source === 'manual' ? <><UserCog className="w-3 h-3 text-sky-300" /> Set by {s.set_by_name || 'admin'}</>
            : pending ? <>Seen on {s.invoices_seen} CIS invoice · recognised after 2</> : null}
        </div>
      </td>
      <td>
        {s.cis ? <RatePills value={s.rate} onChange={(v) => patch({ rate: v }, `${s.supplier_name} → ${rateLabel(v)}`)} disabled={busy} testid={`cis-contractor-rate-${s.id}`} />
          : <span className="text-xs text-gray-400">{pending ? 'Not yet recognised' : 'Marked not CIS'}</span>}
      </td>
      <td>
        {s.cis ? (
          <div className="inline-flex rounded-md overflow-hidden border border-white/15" data-testid={`cis-contractor-vat-${s.id}`}>
            {VAT_TREATMENTS.map(o => (
              <button key={o.value} type="button" disabled={busy} onClick={() => patch({ vat_treatment: o.value }, `${s.supplier_name} → ${o.label}`)} title={o.label}
                className={`px-2.5 h-7 text-xs transition-colors ${(s.vat_treatment || 'no_vat') === o.value ? 'bg-[#F5A623] text-[#1E2530] font-medium' : 'bg-[#1E2530] text-gray-300 hover:bg-white/5'}`}
                data-testid={`cis-contractor-vat-${s.id}-${o.value}`}>{o.value === 'no_vat' ? 'No VAT' : 'Reverse charge'}</button>
            ))}
          </div>
        ) : <span className="text-gray-600">—</span>}
      </td>
      <td>
        <Input value={utr} onChange={e => setUtr(e.target.value)} onBlur={() => { if ((utr || '') !== (s.utr || '')) patch({ utr }, 'UTR saved'); }}
          className={`${field} w-32 font-mono`} placeholder="UTR" data-testid={`cis-contractor-utr-${s.id}`} />
      </td>
      <td className="text-right tabular-nums text-gray-300">{s.posted_invoices || 0}</td>
      <td className="text-right tabular-nums text-[#F5A623] font-semibold">{gbp(s.deduction_pounds)}</td>
      <td className="text-gray-400 text-xs">{uk(s.last_posted_at)}</td>
      <td className="text-right whitespace-nowrap">
        {s.cis
          ? <button type="button" disabled={busy} onClick={() => patch({ cis: false }, `${s.supplier_name} marked not CIS`)} className="text-xs text-gray-400 hover:text-amber-300 underline mr-3" data-testid={`cis-contractor-not-cis-${s.id}`}>Not CIS</button>
          : <button type="button" disabled={busy} onClick={() => patch({ cis: true }, `${s.supplier_name} marked CIS`)} className="text-xs text-emerald-300 hover:underline mr-3" data-testid={`cis-contractor-mark-cis-${s.id}`}>Mark CIS</button>}
        <button type="button" disabled={busy} onClick={remove} className="text-gray-500 hover:text-red-400" title="Forget this record" data-testid={`cis-contractor-remove-${s.id}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
      </td>
    </tr>
  );
}

export function CisContractorsPanel({ api }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [showOther, setShowOther] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get('/cost-inbox/cis-suppliers'); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load CIS contractors'); setData({ suppliers: [], count: 0 }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const all = data?.suppliers || [];
  const match = (s) => !q || (s.supplier_name || '').toLowerCase().includes(q.toLowerCase());
  const cisList = all.filter(s => s.cis && match(s));
  const others = all.filter(s => !s.cis && match(s));
  const totalDed = cisList.reduce((a, s) => a + Number(s.deduction_pounds || 0), 0);

  return (
    <div className="space-y-4" data-testid="cis-contractors-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-gray-400 max-w-2xl">
          Every sub-contractor the Cost Inbox treats as CIS. Their invoices default to CIS with this deduction status — you can still override on an invoice. A supplier is <span className="text-emerald-300">learned</span> automatically once two of their invoices have been posted as CIS.
        </div>
        <AddContractor api={api} onAdded={load} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cis-contractor-totals">
        {[['CIS contractors', cisList.length, false], ['Standard 20%', cisList.filter(s => Number(s.rate) === 20).length, false], ['Higher 30% · Gross', `${cisList.filter(s => Number(s.rate) === 30).length} · ${cisList.filter(s => Number(s.rate) === 0).length}`, false], ['CIS deducted · all time', totalDed, true]].map(([l, v, money]) => (
          <div key={l} className="rounded-lg bg-[#2A3240] border border-white/10 p-3">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{l}</div>
            <div className={`text-xl font-bold tabular-nums ${money ? 'text-[#F5A623]' : 'text-white'}`}>{money ? gbp(v) : v}</div>
          </div>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={e => setQ(e.target.value)} className={`${field} pl-8 h-9`} placeholder="Find a contractor…" data-testid="cis-contractor-search" />
      </div>

      {!data ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div> : cisList.length === 0 ? (
        <div className="py-12 text-center text-gray-500 rounded-lg bg-[#2A3240] border border-white/10" data-testid="cis-contractors-empty">
          {q ? 'No CIS contractor matches that search.' : 'No CIS contractors yet — add one, or post two CIS invoices from a supplier and the reader will learn them.'}
        </div>
      ) : (
        <div className="rounded-lg bg-[#2A3240] border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-gray-500">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
                <th>Contractor</th><th>CIS status</th><th>VAT</th><th>UTR</th><th className="text-right">Invoices</th><th className="text-right">CIS deducted</th><th>Last invoice</th><th />
              </tr>
            </thead>
            <tbody>{cisList.map(s => <ContractorRow key={s.id} s={s} api={api} onChanged={load} />)}</tbody>
          </table>
        </div>
      )}

      {others.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowOther(v => !v)} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="cis-show-others">
            {showOther ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {others.length} supplier{others.length === 1 ? '' : 's'} seen on a CIS invoice but not (yet) recognised, or marked not CIS
          </button>
          {showOther && (
            <div className="mt-2 rounded-lg bg-[#2A3240] border border-white/10 overflow-x-auto" data-testid="cis-others-table">
              <table className="w-full text-sm">
                <tbody>{others.map(s => <ContractorRow key={s.id} s={s} api={api} onChanged={load} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
